import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MasterDataAutocomplete } from './MasterDataAutocomplete';
import { toast } from 'sonner';
import { addMasterData, getMasterData, getFabricTypes } from '../api';
import { Plus, AlertTriangle } from 'lucide-react';

/**
 * Compact dialog for adding a new Trade Item directly from Order Entry/Edit forms.
 * Supports ALT+C keyboard shortcut and "+ Add New Trade Item" dropdown option.
 * 
 * Features:
 * - Duplicate detection (case-insensitive, whitespace-normalized)
 * - Auto-captures audit fields (created_at, created_by)
 * - Returns newly created item for auto-selection in parent form
 */
export default function AddTradeItemDialog({
  open,
  onOpenChange,
  onItemCreated,
  defaultFabricType = 'Printed', // 'Printed' or 'Dyed' based on which section triggered
  suppliers = [],
}) {
  const [loading, setLoading] = useState(false);
  const [fabricTypes, setFabricTypes] = useState(['Printed', 'Dyed', 'Yarn Dyed']);
  const [existingTradeNames, setExistingTradeNames] = useState([]);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const nameInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    fabric_type: defaultFabricType,
    si_unit: 'Metre',
    supplier_id: '__unassigned__',
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm({
        name: '',
        fabric_type: defaultFabricType,
        si_unit: 'Metre',
        supplier_id: '__unassigned__',
      });
      setDuplicateWarning(null);
      // Focus name input after dialog opens
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  }, [open, defaultFabricType]);

  // Fetch fabric types and existing trade names when dialog opens
  useEffect(() => {
    if (open) {
      fetchFabricTypes();
      fetchExistingTradeNames();
    }
  }, [open]);

  const fetchFabricTypes = async () => {
    try {
      const response = await getFabricTypes();
      if (response.data?.fabric_types) {
        setFabricTypes(response.data.fabric_types);
      }
    } catch (error) {
      console.error('Failed to fetch fabric types:', error);
    }
  };

  const fetchExistingTradeNames = async () => {
    try {
      const response = await getMasterData('trade_name');
      setExistingTradeNames(response.data || []);
    } catch (error) {
      console.error('Failed to fetch existing trade names:', error);
    }
  };

  /**
   * Normalize string for comparison:
   * - Trim leading/trailing whitespace
   * - Collapse multiple internal spaces to single space
   * - Convert to lowercase
   */
  const normalizeString = (str) => {
    if (!str) return '';
    return str.trim().replace(/\s+/g, ' ').toLowerCase();
  };

  /**
   * Check for duplicate trade items
   * Returns matching item if found, null otherwise
   */
  const checkForDuplicate = (name, supplierId) => {
    const normalizedName = normalizeString(name);
    if (!normalizedName) return null;

    const effectiveSupplierId = supplierId === '__unassigned__' ? null : supplierId;

    return existingTradeNames.find((item) => {
      const itemNormalizedName = normalizeString(item.name);
      const itemSupplierId = item.supplier_id || null;

      // Match if normalized names are equal AND supplier is the same
      return (
        itemNormalizedName === normalizedName &&
        itemSupplierId === effectiveSupplierId
      );
    });
  };

  /**
   * Find similar trade items (for warning purposes)
   * Returns items with similar names regardless of supplier
   */
  const findSimilarItems = (name) => {
    const normalizedName = normalizeString(name);
    if (!normalizedName) return [];

    return existingTradeNames.filter((item) => {
      const itemNormalizedName = normalizeString(item.name);
      return itemNormalizedName === normalizedName;
    });
  };

  // Check for duplicates when name or supplier changes
  useEffect(() => {
    if (!form.name.trim()) {
      setDuplicateWarning(null);
      return;
    }

    const duplicate = checkForDuplicate(form.name, form.supplier_id);
    if (duplicate) {
      const supplierName = duplicate.supplier_name || 'Unassigned';
      setDuplicateWarning({
        type: 'exact',
        message: `Exact match exists: "${duplicate.name}" (Supplier: ${supplierName})`,
        existingItem: duplicate,
      });
    } else {
      const similarItems = findSimilarItems(form.name);
      if (similarItems.length > 0) {
        const suppliers = similarItems.map(
          (i) => i.supplier_name || 'Unassigned'
        );
        setDuplicateWarning({
          type: 'similar',
          message: `Similar item exists with other suppliers: ${suppliers.join(', ')}`,
          existingItems: similarItems,
        });
      } else {
        setDuplicateWarning(null);
      }
    }
  }, [form.name, form.supplier_id, existingTradeNames]);

  const handleSave = async (forceCreate = false) => {
    // Validation
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      toast.error('Trade Name is required');
      nameInputRef.current?.focus();
      return;
    }
    if (!form.fabric_type) {
      toast.error('Fabric Type is required');
      return;
    }
    if (!form.si_unit) {
      toast.error('SI Unit is required');
      return;
    }

    // Check for exact duplicate (block unless forceCreate)
    if (!forceCreate && duplicateWarning?.type === 'exact') {
      // Show confirmation - user must explicitly confirm
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: trimmedName,
        fabric_type: form.fabric_type,
        si_unit: form.si_unit,
        supplier_id: form.supplier_id === '__unassigned__' ? null : form.supplier_id,
        is_active: true,
      };

      const response = await addMasterData('trade_name', payload);
      
      toast.success(`Trade Item "${trimmedName}" created successfully`);
      
      // Call parent callback with the new item
      if (onItemCreated) {
        onItemCreated(response.data);
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create trade item:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create trade item';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExisting = () => {
    if (duplicateWarning?.existingItem && onItemCreated) {
      onItemCreated(duplicateWarning.existingItem);
      onOpenChange(false);
    }
  };

  const handleKeyDown = (e) => {
    // Allow Escape to close dialog
    if (e.key === 'Escape') {
      onOpenChange(false);
    }
    // Allow Enter to submit (if not in a select)
    if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') {
      e.preventDefault();
      if (duplicateWarning?.type !== 'exact') {
        handleSave();
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-white sm:max-w-md" 
        onKeyDown={handleKeyDown}
        data-testid="add-trade-item-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-maroon-950">
            <Plus className="h-5 w-5" />
            Add New Trade Item
          </DialogTitle>
          <DialogDescription className="sr-only">
            Create a new trade item that will be added to the master data and auto-selected in the order form.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Trade Name */}
          <div>
            <Label htmlFor="trade-item-name" className="text-sm font-medium text-maroon-700">
              Trade Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="trade-item-name"
              ref={nameInputRef}
              data-testid="trade-item-name-input"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Enter trade name"
              className="mt-1"
              autoComplete="off"
            />
          </div>

          {/* Duplicate Warning */}
          {duplicateWarning && (
            <div
              className={`p-3 rounded-lg border ${
                duplicateWarning.type === 'exact'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}
              data-testid="duplicate-warning"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">{duplicateWarning.message}</p>
                  {duplicateWarning.type === 'exact' && (
                    <div className="mt-2 flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleSelectExisting}
                        className="h-7 text-xs"
                        data-testid="select-existing-btn"
                      >
                        Select Existing
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleSave(true)}
                        className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                        data-testid="create-anyway-btn"
                      >
                        Create Anyway
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Fabric Type & SI Unit Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="trade-item-fabric-type" className="text-sm font-medium text-maroon-700">
                Fabric Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.fabric_type}
                onValueChange={(value) => setForm((prev) => ({ ...prev, fabric_type: value }))}
              >
                <SelectTrigger data-testid="trade-item-fabric-type" className="mt-1">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {fabricTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="trade-item-si-unit" className="text-sm font-medium text-maroon-700">
                SI Unit <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.si_unit}
                onValueChange={(value) => setForm((prev) => ({ ...prev, si_unit: value }))}
              >
                <SelectTrigger data-testid="trade-item-si-unit" className="mt-1">
                  <SelectValue placeholder="Select unit..." />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="Metre">Metre</SelectItem>
                  <SelectItem value="Kg">Kg</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Supplier (Optional) */}
          <div>
            <Label htmlFor="trade-item-supplier" className="text-sm font-medium text-maroon-700">
              Supplier <span className="text-maroon-400 text-xs">(Optional)</span>
            </Label>
            <MasterDataAutocomplete
              value={form.supplier_id}
              onValueChange={(value) => setForm((prev) => ({ ...prev, supplier_id: value }))}
              options={[
                { value: '__unassigned__', label: 'Unassigned', pinned: true },
                ...suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name })),
              ]}
              placeholder="Select supplier..."
              searchPlaceholder="Search suppliers..."
              emptyMessage="No matching supplier in Master Data"
              className="mt-1"
              testId="trade-item-supplier"
            />
            <p className="text-xs text-maroon-500 mt-1">
              Same trade name can exist for different suppliers
            </p>
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid="cancel-trade-item-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => handleSave()}
            disabled={loading || duplicateWarning?.type === 'exact'}
            className="bg-maroon-900 hover:bg-maroon-800"
            data-testid="save-trade-item-btn"
          >
            {loading ? 'Creating...' : 'Create Trade Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
